
import React from 'react';
import { motion } from 'framer-motion';
import SectionHeader from '@/components/SectionHeader';
import TechServiceCard from './TechServiceCard';
import TechTerminalDisplay from './TechTerminalDisplay';
import TechBackground from './TechBackground';
import { Database, Code, Server, Cpu, Terminal, Smartphone, LayoutDashboard, Cloud } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.5 }
  }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const techServices = [
  {
    id: 'frontend',
    title: 'Frontend',
    description: 'Modern, responsive interfaces with cutting-edge frameworks',
    icon: <Code className="text-[#33C3F0]" strokeWidth={1.5} />,
    color: '#33C3F0',
    features: [
      'React & Next.js',
      'Responsive Design',
      'Interactive UI',
      'PWA Support'
    ],
    code: 'const App = () => {\n  return <div className="app">\n    <Header />\n    <MainContent />\n    <Footer />\n  </div>\n}',
    commandLine: 'npx create-react-app my-app --template typescript'
  },
  {
    id: 'backend',
    title: 'Backend',
    description: 'Scalable server-side solutions with reliable architectures',
    icon: <Database className="text-[#9b87f5]" strokeWidth={1.5} />,
    color: '#9b87f5',
    features: [
      'API Development',
      'Database Design',
      'Authentication',
      'Microservices'
    ],
    code: 'app.get(\'/api/data\', async (req, res) => {\n  try {\n    const data = await getData();\n    res.json(data);\n  } catch (err) {\n    res.status(500).json({ error: err.message });\n  }\n});',
    commandLine: 'npm install express mongoose jsonwebtoken'
  },
  {
    id: 'mobile',
    title: 'Mobile',
    description: 'Native and cross-platform applications for iOS and Android',
    icon: <Smartphone className="text-[#FF6B6B]" strokeWidth={1.5} />,
    color: '#FF6B6B',
    features: [
      'React Native',
      'iOS Development',
      'Android Development',
      'App Store Publishing'
    ],
    code: 'export default function App() {\n  return (\n    <NavigationContainer>\n      <Stack.Navigator>\n        <Stack.Screen name="Home" component={HomeScreen} />\n      </Stack.Navigator>\n    </NavigationContainer>\n  );\n}',
    commandLine: 'npx react-native init MyApp --template react-native-template-typescript'
  },
  {
    id: 'devops',
    title: 'DevOps',
    description: 'Continuous integration and deployment with cloud infrastructure',
    icon: <Cloud className="text-[#20B2AA]" strokeWidth={1.5} />,
    color: '#20B2AA',
    features: [
      'CI/CD Pipelines',
      'Docker & Kubernetes',
      'AWS & Azure',
      'Infrastructure as Code'
    ],
    code: 'resource "aws_instance" "app_server" {\n  ami           = "ami-0c55b159cbfafe1f0"\n  instance_type = "t2.micro"\n  tags = {\n    Name = "ExampleAppServerInstance"\n  }\n}',
    commandLine: 'docker-compose up -d && kubectl apply -f deployment.yaml'
  },
  {
    id: 'fullstack',
    title: 'Full Stack',
    description: 'End-to-end solutions from database to user interface',
    icon: <Cpu className="text-[#8B5CF6]" strokeWidth={1.5} />,
    color: '#8B5CF6',
    features: [
      'Complete Architecture',
      'Integrated Solutions',
      'Full Application Lifecycle',
      'Performance Optimization'
    ],
    code: 'export async function getServerSideProps() {\n  const res = await fetch(\'https://api.example.com/data\');\n  const data = await res.json();\n  return { props: { data } };\n}',
    commandLine: 'npm run build && npm run deploy'
  }
];

const TechServices = () => {
  const [activeService, setActiveService] = React.useState(techServices[0]);
  
  return (
    <section id="tech-services" className="relative py-12 md:py-16 overflow-hidden">
      {/* Tech-style grid pattern background */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxwYXRoIGQ9Ik0wIDBoMXY0MGgtMXpNMzkgMGgxdjQwaC0xek0wIDBoNDB2MWgtNDB6TTAgMzloNDB2MWgtNDB6IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz48L2c+PC9zdmc+')] bg-[#001233] opacity-30"></div>
      
      {/* Blurred tech glow effects */}
      <div className="absolute top-20 right-20 w-72 h-72 rounded-full bg-[#33C3F0]/10 blur-3xl"></div>
      <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#9b87f5]/10 blur-3xl"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <SectionHeader
            title="Tech Stack"
            subtitle="End-to-end solutions powered by cutting-edge technologies"
            emoji="💻"
            titleColor="text-white"
          />
        </motion.div>
        
        <div className="mt-12">
          <TechBackground>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left column: Tech categories */}
              <motion.div 
                className="lg:col-span-1"
                initial="hidden"
                whileInView="visible"
                variants={containerVariants}
                viewport={{ once: true }}
              >
                <div className="bg-[#1A1F2C]/70 backdrop-blur-sm border border-white/10 rounded-xl p-4">
                  <h3 className="text-white/80 text-xs uppercase font-mono mb-4 tracking-wider">Technology Domains</h3>
                  
                  <motion.div className="space-y-2">
                    {techServices.map((service) => (
                      <motion.div
                        key={service.id}
                        variants={fadeInUp}
                        className={`p-3 rounded-lg flex items-center cursor-pointer transition-all hover:bg-white/5 ${activeService.id === service.id ? 'bg-white/10 border-l-2' : 'border-l-2 border-transparent'}`}
                        style={{ 
                          borderLeftColor: activeService.id === service.id ? service.color : 'transparent',
                        }}
                        onClick={() => setActiveService(service)}
                      >
                        <div 
                          className="p-2 rounded-lg mr-3 flex-shrink-0"
                          style={{ 
                            backgroundColor: `${service.color}20`,
                          }}
                        >
                          {service.icon}
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{service.title}</h4>
                          <p className="text-white/60 text-xs">{service.features.slice(0, 2).join(' • ')}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
              
              {/* Right column: Active service details with tech visualization */}
              <motion.div 
                className="lg:col-span-2 flex flex-col"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                key={activeService.id}
              >
                <div className="bg-[#1A1F2C]/70 backdrop-blur-sm border border-white/10 rounded-xl p-6 h-full">
                  <div className="flex flex-col md:flex-row justify-between mb-6">
                    <div>
                      <div className="flex items-center">
                        <div 
                          className={`p-3 rounded-lg mr-4 flex-shrink-0`}
                          style={{ backgroundColor: `${activeService.color}20` }}
                        >
                          {activeService.icon}
                        </div>
                        <div>
                          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{activeService.title}</h2>
                          <p className="text-white/70">{activeService.description}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Tech visualization - Terminal-like display */}
                  <TechTerminalDisplay 
                    code={activeService.code} 
                    commandLine={activeService.commandLine}
                    color={activeService.color}
                  />
                  
                  {/* Features list */}
                  <div className="mt-8 grid grid-cols-2 gap-4">
                    {activeService.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div 
                          className="w-2 h-2 rounded-full mr-2"
                          style={{ backgroundColor: activeService.color }}
                        ></div>
                        <span className="text-white/80">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          
            <motion.div
              className="mt-10 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <a 
                href="#contact" 
                className="inline-flex items-center justify-center px-8 py-3 text-base font-medium text-white bg-gradient-to-r from-[#33C3F0] to-[#9b87f5] rounded-lg hover:opacity-90 transition-opacity duration-300 shadow-lg hover:shadow-blue-500/20 border border-white/10"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Discuss Your Project
              </a>
            </motion.div>
          </TechBackground>
        </div>
      </div>
    </section>
  );
};

export default TechServices;
